import React from 'react';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import styled from 'styled-components';

const TutorialsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const VideoSection = styled.div`
  margin-bottom: 4rem;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background-color: var(--background-dark);
  border-radius: var(--border-radius);
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: var(--box-shadow);
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }
  
  p {
    font-size: 1rem;
    max-width: 500px;
    text-align: center;
    margin-bottom: 1.5rem;
  }
`;

const TutorialsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TutorialCard = styled.div`
  background-color: var(--background-light);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
  
  .thumbnail {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    background-color: var(--background-dark);
    
    .play-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60px;
      height: 60px;
      background-color: rgba(0, 0, 0, 0.7);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      svg {
        width: 24px;
        height: 24px;
        color: white;
      }
    }
  }
  
  .content {
    padding: 1.5rem;
    
    h3 {
      font-size: 1.25rem;
      margin-bottom: 0.75rem;
      color: var(--text-color);
    }
    
    p {
      font-size: 0.875rem;
      color: var(--text-secondary);
      margin-bottom: 1rem;
    }
    
    .meta {
      display: flex;
      justify-content: space-between;
      font-size: 0.75rem;
      color: var(--text-secondary);
    }
  }
`;

const ComingSoonSection = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background-color: var(--background-light);
  border-radius: var(--border-radius);
  margin-top: 4rem;
  
  h3 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }
  
  p {
    font-size: 1.125rem;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const TutorialsPage: React.FC = () => {
  return (
    <Layout>
      <SEO
        title="USDT Flashing Tutorial | Video Guides Flash USDT Website"
        description="Learn how to use Flash USDT Website with our comprehensive USDT flashing tutorials. Step-by-step video guides for beginners and advanced users."
        keywords="USDT flashing tutorial, tutorials, video guides, how to use USDT flasher, crypto tutorial"
      />
      
      <Section
        title="Video Tutorials"
        subtitle="Learn how to use Flash USDT Website with our step-by-step video tutorials. From basic setup to advanced features, we've got you covered."
        background="light"
        padding="large"
      >
        <TutorialsContainer>
          <VideoSection>
            <h2>Getting Started with Flash USDT Website</h2>
            <VideoContainer>
              <iframe
                src="https://player.vimeo.com/video/1064098940"
                width="640"
                height="360"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              ></iframe>
            </VideoContainer>
            
            <p>
              This comprehensive tutorial will walk you through the entire process of setting up and using Flash USDT Website. You'll learn how to install the software, connect your wallet, configure the settings, and perform your first USDT flash. Perfect for beginners who are just getting started with cryptocurrency flashing.
            </p>
          </VideoSection>
          
          <h2>More Tutorials</h2>
          <TutorialsList>
            <TutorialCard>
              <div className="thumbnail">
                <div className="play-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                </div>
              </div>
              <div className="content">
                <h3>Wallet Connection Guide</h3>
                <p>
                  Learn how to securely connect your cryptocurrency wallet to Flash USDT Website for seamless transactions.
                </p>
                <div className="meta">
                  <span>Duration: 8:45</span>
                  <span>Coming Soon</span>
                </div>
              </div>
            </TutorialCard>
            
            <TutorialCard>
              <div className="thumbnail">
                <div className="play-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                </div>
              </div>
              <div className="content">
                <h3>Advanced Settings Configuration</h3>
                <p>
                  Discover how to optimize your Flash USDT Website settings for maximum efficiency and success rate.
                </p>
                <div className="meta">
                  <span>Duration: 12:20</span>
                  <span>Coming Soon</span>
                </div>
              </div>
            </TutorialCard>
            
            <TutorialCard>
              <div className="thumbnail">
                <div className="play-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                </div>
              </div>
              <div className="content">
                <h3>Multi-Network Flashing</h3>
                <p>
                  Master the technique of flashing USDT across multiple blockchain networks for increased profits.
                </p>
                <div className="meta">
                  <span>Duration: 15:30</span>
                  <span>Coming Soon</span>
                </div>
              </div>
            </TutorialCard>
            
            <TutorialCard>
              <div className="thumbnail">
                <div className="play-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                  </svg>
                </div>
              </div>
              <div className="content">
                <h3>Troubleshooting Common Issues</h3>
                <p>
                  Solutions to common problems you might encounter while using Flash USDT Website and how to resolve them.
                </p>
                <div className="meta">
                  <span>Duration: 10:15</span>
                  <span>Coming Soon</span>
                </div>
              </div>
            </TutorialCard>
          </TutorialsList>
          
          <ComingSoonSection>
            <h3>More Tutorials Coming Soon</h3>
            <p>
              We're constantly working on new tutorial videos to help you get the most out of Flash USDT Website. Subscribe to our newsletter to be notified when new tutorials are available.
            </p>
            <Button variant="primary">
              Subscribe to Updates
            </Button>
          </ComingSoonSection>
        </TutorialsContainer>
      </Section>
    </Layout>
  );
};

export default TutorialsPage;
